import React, { useLayoutEffect, useRef, useState } from 'react';
import T from 'prop-types';

import SharesChart from './SharesChart';
import ShareHeatmap from './ShareHeatmap';
import WithTooltipIcon from '../WithTooltipIcon';
import ChartTypeToggle from '../ChartTypeToggle';
import SwitchChangeType from '../SwitchChangeType';
import { MarketShareDropdown, marketShareMetrics } from './metrics';
import { isYoYChange, periodChangeTypeCaptures } from '../../../../../utils/change-types';

const decorateMarketShareOption = (option, heatmapChangeType) => {
  if (option.value.includes('over_change')) {
    return {
      ...option,
      label: `${option.label} (${periodChangeTypeCaptures[heatmapChangeType] || 'PoP'})`,
    };
  }

  return option;
};

const emptyObject = {};

const MarketShareChart = ({
  apps,
  series,
  isPending,
  isPress,
  partialFilters,
  maxDate,
  cumulativeCaption,
  heatmapChangeType,
  onHeatmapChangeTypeChange,
  withEdgeLines,
}) => {
  const contentRef = useRef(null);

  const [selectedMetric, setSelectedMetric] = useState(Object.keys(marketShareMetrics)[0]);
  const [chartType, setChartType] = useState('column');
  const [contentHeight, setContentHeight] = useState(364); // To be able to animate content height change

  const isCumulative = selectedMetric.includes('cumulative');
  const isOverChange = selectedMetric.includes('over');
  const hasHeatmapChangeTypeToggle = Boolean(heatmapChangeType && onHeatmapChangeTypeChange);

  const showChartTypeToggle = !isCumulative && !isOverChange;
  const showHeatmapChangeTypeToggle = hasHeatmapChangeTypeToggle && isOverChange;

  const metricKey = (() => {
    if (isOverChange) {
      return selectedMetric.replace(
        '_over_change',
        isYoYChange(heatmapChangeType) ? '_yoy_change' : '_pop_change'
      );
    }

    return selectedMetric;
  })();

  const metricSeries = series?.[metricKey] || emptyObject;

  useLayoutEffect(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [isCumulative, isOverChange]);

  return (
    <div className="market-share-chart chart-with-alternative-hovering">
      <header className="market-share-chart__header">
        <div className="d-flex align-items-center gap-4">
          <WithTooltipIcon
            tooltipContent={(() => {
              if (isCumulative && cumulativeCaption?.placement === 'title-tooltip') {
                return cumulativeCaption.text;
              }
              return null;
            })()}
          >
            <MarketShareDropdown
              selected={selectedMetric}
              decorateOption={
                !hasHeatmapChangeTypeToggle
                  ? (option) => decorateMarketShareOption(option, heatmapChangeType)
                  : undefined
              }
              onChange={setSelectedMetric}
            />
          </WithTooltipIcon>
        </div>
        {showChartTypeToggle && (
          <ChartTypeToggle active={chartType} disabled={isPending} onChange={setChartType} />
        )}
        {showHeatmapChangeTypeToggle && (
          <SwitchChangeType
            active={heatmapChangeType}
            disabled={isPending}
            onChange={onHeatmapChangeTypeChange}
          />
        )}
      </header>
      <div className="market-share-chart__content" style={{ height: contentHeight }}>
        <div ref={contentRef} className="market-share-chart__content-inner">
          {isOverChange ? (
            <ShareHeatmap
              metricSeries={metricSeries}
              apps={apps}
              isPending={isPending}
              maxDate={maxDate}
            />
          ) : (
            <SharesChart
              key={isCumulative ? 'cumulative-share' : 'market-share'} // force remount on type change to animate switch
              metricSeries={metricSeries}
              chartType={chartType}
              apps={apps}
              isPending={isPending}
              isCumulative={isCumulative}
              partialFilters={partialFilters}
              chartCaption={
                isCumulative &&
                cumulativeCaption.placement === 'chart-caption' &&
                cumulativeCaption.text
              }
              isPress={isPress}
              withEdgeLines={withEdgeLines}
            />
          )}
        </div>
      </div>
    </div>
  );
};

MarketShareChart.propTypes = {
  series: T.object,
  apps: T.array,
  isPending: T.bool.isRequired,
  isPress: T.bool,
  partialFilters: T.object.isRequired,
  maxDate: T.oneOfType([T.string, T.number, T.instanceOf(Date)]).isRequired,
  cumulativeCaption: T.shape({
    text: T.node.isRequired,
    placement: T.oneOf(['title-tooltip', 'chart-caption']),
  }),
  heatmapChangeType: T.oneOf(['period_over_period', 'year_over_year']),
  onHeatmapChangeTypeChange: T.func,
  withEdgeLines: T.bool,
};

export default MarketShareChart;
